/**
 * CrewSavedPage ✦ — /crew/saved
 *
 * Bookmarked opportunity list. Identical neon aesthetic to the
 * Feed so visual continuity reads instantly. Tapping a card opens
 * the feed scrolled to that opportunity (?focus=<id>); the
 * un-save button keeps the list in sync optimistically.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookmarkSimple, MapPin, CurrencyGbp, CalendarBlank } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const fmtDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return s; }
};

const SavedCard = ({ opp, onOpen, onUnsave }) => (
  <div
    data-testid={`saved-card-${opp.id}`}
    style={{
      borderRadius: 18,
      background: PANEL,
      border: `1px solid ${PANEL_BORDER}`,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {opp.photo_url ? (
      <button
        onClick={() => onOpen(opp)}
        data-testid={`saved-open-${opp.id}`}
        style={{
          padding: 0, border: 'none', cursor: 'pointer',
          background: `url(${opp.photo_url}) center/cover no-repeat`,
          height: 160,
        }}
        aria-label={`Open ${opp.title}`}
      />
    ) : (
      <button
        onClick={() => onOpen(opp)}
        data-testid={`saved-open-${opp.id}`}
        style={{
          padding: 0, border: 'none', cursor: 'pointer',
          background: `linear-gradient(135deg, ${PURPLE}55, ${PINK}33)`,
          height: 100,
        }}
        aria-label={`Open ${opp.title}`}
      />
    )}
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{
        margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 17,
        color: TEXT, letterSpacing: '-0.01em',
      }}>{opp.title}</p>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10,
        fontFamily: FONT_STACK, fontSize: 12, color: TEXT_MUTED,
      }}>
        {opp.event_date && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CalendarBlank size={12} weight="bold" /> {fmtDate(opp.event_date)}
          </span>
        )}
        {opp.location && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={12} weight="bold" /> {opp.location}
          </span>
        )}
        {opp.pay && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: BLUE, fontWeight: 700 }}>
            <CurrencyGbp size={12} weight="bold" /> {opp.pay}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button
          onClick={() => onOpen(opp)}
          data-testid={`saved-apply-${opp.id}`}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 999, border: 'none',
            cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
            fontSize: 13, fontWeight: 800,
            background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
            boxShadow: `0 6px 18px ${PURPLE}44`,
          }}
        >
          Open in feed
        </button>
        <button
          onClick={() => onUnsave(opp)}
          data-testid={`saved-remove-${opp.id}`}
          style={{
            padding: '10px 14px', borderRadius: 999,
            background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
            color: TEXT_MUTED, cursor: 'pointer', fontFamily: FONT_STACK,
            fontSize: 12, fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', gap: 5,
          }}
        >
          <BookmarkSimple size={12} weight="fill" /> Remove
        </button>
      </div>
    </div>
  </div>
);

const CrewSavedPage = () => {
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/crew/me/saved`);
      setOpps(r.data?.opportunities || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/saved');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load saved opportunities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const unsave = async (opp) => {
    // Optimistic remove for instant feedback
    setOpps((cur) => cur.filter((o) => o.id !== opp.id));
    try {
      await axios.delete(`${API}/crew/opportunities/${opp.id}/save`);
    } catch (err) {
      // Reload to recover from any failure
      load();
    }
  };

  const open = (opp) => {
    navigate(`/crew/feed?focus=${opp.id}`);
  };

  return (
    <div
      data-testid="crew-saved-page"
      style={{
        minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
        padding: 'clamp(20px, 5vw, 56px) clamp(16px, 5vw, 32px) 80px',
      }}
    >
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 18% 14%, ${PURPLE}1F 0%, transparent 42%), radial-gradient(circle at 85% 80%, ${PINK}1A 0%, transparent 42%)`,
      }} />

      <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => navigate('/crew/feed')}
          data-testid="saved-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Feed
        </button>

        <p style={{
          margin: '0 0 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ Bookmarked
        </p>
        <h1 style={{
          margin: '0 0 8px', fontFamily: FONT_STACK, fontSize: 30, fontWeight: 900,
          letterSpacing: '-0.02em',
        }}>
          Your saved nights
        </h1>
        <p style={{ color: TEXT_MUTED, fontSize: 14, marginBottom: 28, lineHeight: 1.55 }}>
          The opportunities you bookmarked from the feed. Open one to apply, or remove to clear it.
        </p>

        {loading && (
          <p data-testid="saved-loading" style={{ color: TEXT_MUTED, fontSize: 14 }}>Loading…</p>
        )}

        {!loading && error && (
          <p data-testid="saved-error" style={{ color: PINK, fontSize: 14 }}>{error}</p>
        )}

        {!loading && !error && opps.length === 0 && (
          <div
            data-testid="saved-empty"
            style={{
              padding: 28, borderRadius: 18, background: PANEL,
              border: `1px dashed ${PANEL_BORDER}`,
              textAlign: 'center',
            }}
          >
            <BookmarkSimple size={28} weight="fill" color={PINK} />
            <p style={{ margin: '12px 0 6px', fontFamily: FONT_STACK, fontWeight: 800, fontSize: 18 }}>
              Nothing saved yet
            </p>
            <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
              Tap the bookmark on any opportunity card to keep it here for later.
            </p>
            <button
              onClick={() => navigate('/crew/feed')}
              data-testid="saved-go-feed"
              style={{
                marginTop: 16, padding: '12px 22px', borderRadius: 999, border: 'none',
                cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
                fontSize: 14, fontWeight: 800,
                background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                boxShadow: `0 8px 22px ${PURPLE}55`,
              }}
            >
              Browse the feed
            </button>
          </div>
        )}

        {!loading && !error && opps.length > 0 && (
          <div
            data-testid="saved-list"
            style={{
              display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            }}
          >
            {opps.map((opp) => (
              <SavedCard key={opp.id} opp={opp} onOpen={open} onUnsave={unsave} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrewSavedPage;
